import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { PayGroupsLeftOut } from '@efacture/core';

// ─────────────────────────────────────────────────────────────
// Aides
// ─────────────────────────────────────────────────────────────

/**
 * Les colonnes Decimal de Prisma sont sérialisées en chaînes JSON
 * (ex. "5000" et non 5000) — conversion avant toute arithmétique.
 */
import { toNum, type DecimalString } from '@efacture/web-billing/lib/decimal';

export { toNum };

// ─────────────────────────────────────────────────────────────
// Types — plan comptable (sélecteurs de comptes)
// ─────────────────────────────────────────────────────────────

export interface AccountingAccount {
  id: string;
  number: string;
  label: string;
  class: number;
  normalBalance: 'DEBIT' | 'CREDIT';
  isPostable: boolean;
  isLetterable: boolean;
  isReconcilable: boolean;
  isSystem: boolean;
  isActive: boolean;
}

export interface AccountingAccountsParams {
  class?: number;
  search?: string;
  postableOnly?: boolean;
}

export interface AccountingAccountsResponse {
  accounts: AccountingAccount[];
}

// ─────────────────────────────────────────────────────────────
// Types — lettrage
// ─────────────────────────────────────────────────────────────

/** Ligne d'écriture non lettrée (JournalLine + solde progressif). */
export interface UnletteredLine {
  id: string;
  entryId: string;
  lineNumber: number;
  accountId: string;
  accountNumber: string;
  accountLabel: string;
  date: string;
  journalCode: string;
  debit: DecimalString;
  credit: DecimalString;
  label: string | null;
  auxiliaryType: string | null;
  auxiliaryId: string | null;
  letteringId: string | null;
  /** Number, label and reference of the line's entry. */
  entryNumber: string | null;
  entryLabel: string;
  entryReference: string | null;
  /** Origin document of an imported open item (reference, date, due date). */
  documentRef?: string | null;
  documentDate?: string | null;
  dueDate?: string | null;
  /** Solde progressif (débits − crédits cumulés). */
  solde: number;
}

export interface UnletteredLinesResponse {
  lines: UnletteredLine[];
  /** Final balance of the account over its unlettered lines. */
  solde: number;
  /** Per-employee pay lines aggregated by the server (no payroll:read). */
  employeeDetailWithheld?: boolean;
  /** Set when pay groups are missing from `lines` and `solde` (no payroll:read). */
  payGroupsLeftOut?: PayGroupsLeftOut | null;
}

export interface Lettering {
  id: string;
  accountId: string;
  code: string;
  createdAt: string;
  createdById: string;
  lineIds: string[];
}

export interface CreateLetteringInput {
  accountId: string;
  lineIds: string[];
  /** Accept a selection that does not balance: a partial lettering. */
  partial?: boolean;
  /** Add the lines to this partial lettering. */
  letteringId?: string;
}

export interface UnletterResult {
  letteringId: string;
  code: string;
  accountId: string;
  lineIds: string[];
  auditNote: string;
  /** Contre-passations of the exchange-difference entries; their lines are lettered anew. */
  fxReversalEntryIds: string[];
}

// ─────────────────────────────────────────────────────────────
// Types — grand livre (lignes lettrées / lignes à rapprocher)
// ─────────────────────────────────────────────────────────────

export interface LedgerLine {
  id: string;
  date: string;
  journalCode: string;
  entryId: string;
  entryNumber: string | null;
  entryLabel: string;
  accountNumber: string;
  accountLabel: string;
  label: string | null;
  reference: string | null;
  debit: DecimalString;
  credit: DecimalString;
  /** Solde progressif (débit − crédit cumulés). */
  balance: number;
  auxiliaryType: string | null;
  auxiliaryId: string | null;
  letteringId: string | null;
  /** Rapprochement bancaire : non null = ligne déjà rapprochée. */
  reconciledAt: string | null;
  statementLineId: string | null;
}

export interface LedgerParams {
  accountNumber: string;
  from?: string;
  to?: string;
  auxiliaryId?: string;
  /** Restricts to already-lettered lines (LetteringPage's own display). */
  onlyLettered?: boolean;
  /** Restricts to lines not yet matched to a bank statement (ReconciliationPage). */
  onlyUnreconciled?: boolean;
  /** Keyset pagination: resumes after the previous page's `nextCursor`. */
  cursor?: { date: string; id: string } | undefined;
}

export interface LedgerResponse {
  accountNumber: string;
  lines: LedgerLine[];
  totals: { totalDebit: number; totalCredit: number; balance: number };
  /** Pass as `cursor` to fetch the next page; null once exhausted. */
  nextCursor: { date: string; id: string } | null;
  /** True when the API capped `lines` (oldest first) below the full history. */
  truncated?: boolean;
}

// ─────────────────────────────────────────────────────────────
// Types — rapprochement bancaire
// ─────────────────────────────────────────────────────────────

export interface BankStatementLine {
  id: string;
  statementId: string;
  date: string;
  label: string;
  /** Montant signé: + crédit banque, − débit banque (Decimal → string). */
  amount: DecimalString;
  matchedLineId: string | null;
  matchedAt: string | null;
}

export interface BankStatement {
  id: string;
  accountId: string;
  label: string;
  startDate: string;
  endDate: string;
  openingBalance: DecimalString;
  closingBalance: DecimalString;
  status: 'DRAFT' | 'VALIDATED';
  lines: BankStatementLine[];
}

export interface CreateStatementLineInput {
  date: string;
  label: string;
  amount: number;
}

export interface CreateStatementInput {
  accountId: string;
  label: string;
  startDate: string;
  endDate: string;
  openingBalance: number;
  closingBalance: number;
  lines: CreateStatementLineInput[];
}

export interface StatementsResponse {
  statements: BankStatement[];
}

export interface MatchInput {
  statementId: string;
  statementLineId: string;
  journalLineId: string;
}

export interface UnmatchInput {
  statementId: string;
  statementLineId: string;
}

export interface ValidateStatementReport {
  ok: boolean;
  /** Nombre de lignes de relevé non rapprochées. */
  unmatchedCount: number;
  /** Écart = solde de clôture − solde d'ouverture − Σ lignes rapprochées. */
  ecart: number;
  statement: BankStatement;
}

// ─────────────────────────────────────────────────────────────
// Types — balance âgée clients (comptes 411x non lettrés)
// ─────────────────────────────────────────────────────────────

export interface AgedBalanceBuckets {
  /** 0 à 30 jours (inclus). */
  b0_30: number;
  /** 31 à 60 jours (inclus). */
  b31_60: number;
  /** 61 à 90 jours (inclus). */
  b61_90: number;
  /** Plus de 90 jours. */
  b90plus: number;
  /** Somme des quatre tranches. */
  total: number;
}

export interface AgedBalanceRow extends AgedBalanceBuckets {
  /** Client.id — null pour les lignes 411x sans tiers rattaché. */
  clientId: string | null;
  clientName: string;
}

// ─────────────────────────────────────────────────────────────
// Types — suivi de la comptabilisation automatique
// ─────────────────────────────────────────────────────────────

export interface PostingFailure {
  id: string;
  sourceType: string;
  sourceId: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
}

export interface PostingStatusResponse {
  /** Compteurs par statut: PENDING | POSTED | FAILED | SKIPPED. */
  counts: Record<string, number>;
  recentFailures: PostingFailure[];
}

/**
 * The settlement of a provisional document that waits for a regularisation
 * (arrêté n°2025-0049, art. 6 g)): the only rows the accountant may mark
 * « Comptabilisé manuellement ».
 */
export interface PostingWaitingRow {
  /** The outbox row, which the manual mark takes. */
  id: string;
  paymentId: string;
  amount: string;
  method: string;
  paidAt: string;
  invoiceId: string;
  /** Number of the provisional document. */
  invoiceNumber: string;
  clientName: string | null;
  /** Deadline of the provisional's regularisation (issue + 48 h), ISO 8601. */
  regularisationDueAt: string;
  /** Where the provisional stands now: still inside its 48 h, or overdue. */
  status: 'A_REGULARISER' | 'EN_RETARD';
  statusLabel: string;
}

export interface PostingWaitingResponse {
  rows: PostingWaitingRow[];
  /** Every waiting settlement, the list being capped. */
  total: number;
}

export interface RetryPostingResult {
  requeued: number;
  posted: number;
  failed: number;
}

// ─────────────────────────────────────────────────────────────
// Types — paramètres (mappings rôle → compte)
// ─────────────────────────────────────────────────────────────

/**
 * Vue fusionnée rôle logique → numéro de compte (17 rôles, défauts
 * SYSCOHADA surchargés par tenant). Les clés viennent du serveur —
 * source de vérité unique (@efacture/accounting).
 */
export interface AccountMappingsResponse {
  /** null: a role the tenant's regime leaves without an account. */
  mappings: Record<string, string | null>;
}

export interface UpdateMappingInput {
  role: string;
  accountNumber: string;
}

// ─────────────────────────────────────────────────────────────
// Clés de requête
// ─────────────────────────────────────────────────────────────

export const accountingOpsKeys = {
  all: ['accounting-ops'] as const,
  accounts: (params: AccountingAccountsParams = {}) =>
    [...accountingOpsKeys.all, 'accounts', params] as const,
  // Le filtre tiers (comptes tiers, spec 2026-07-24 §5.4) fait partie de
  // la clé ; les invalidations utilisent le PRÉFIXE [..., 'unlettered',
  // accountId] pour couvrir toutes les variantes filtrées.
  unlettered: (accountId: string, auxiliaryAccountId?: string) =>
    [
      ...accountingOpsKeys.all,
      'unlettered',
      accountId,
      auxiliaryAccountId ?? 'all',
    ] as const,
  ledger: (params: LedgerParams) =>
    [...accountingOpsKeys.all, 'ledger', params] as const,
  statements: (accountId?: string) =>
    [...accountingOpsKeys.all, 'statements', accountId ?? 'all'] as const,
  statement: (id: string) =>
    [...accountingOpsKeys.all, 'statement', id] as const,
  postingStatus: () => [...accountingOpsKeys.all, 'posting-status'] as const,
  postingWaiting: () => [...accountingOpsKeys.all, 'posting-waiting'] as const,
  mappings: () => [...accountingOpsKeys.all, 'mappings'] as const,
  agedBalance: (asOf?: string) =>
    [...accountingOpsKeys.all, 'aged-balance', asOf ?? 'today'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API
// ─────────────────────────────────────────────────────────────

async function fetchAccounts(
  params: AccountingAccountsParams
): Promise<AccountingAccountsResponse> {
  return apiClient.get<AccountingAccountsResponse>('/accounting/accounts', {
    class: params.class,
    search: params.search,
    // false → omis (même garde que accounting.ts) : ne jamais sérialiser
    // « postableOnly=false » dans la query string.
    postableOnly: params.postableOnly || undefined,
  });
}

async function fetchUnletteredLines(
  accountId: string,
  auxiliaryAccountId?: string
): Promise<UnletteredLinesResponse> {
  return apiClient.get<UnletteredLinesResponse>(
    `/accounting/lettering/${accountId}/unlettered`,
    { auxiliaryAccountId }
  );
}

async function createLettering(
  input: CreateLetteringInput
): Promise<{ lettering: Lettering }> {
  return apiClient.post<{ lettering: Lettering }>('/accounting/lettering', input);
}

async function deleteLettering(
  id: string
): Promise<{ delettrage: UnletterResult }> {
  return apiClient.delete<{ delettrage: UnletterResult }>(
    `/accounting/lettering/${id}`
  );
}

async function fetchLedger(params: LedgerParams): Promise<LedgerResponse> {
  return apiClient.get<LedgerResponse>('/accounting/reports/grand-livre', {
    accountNumber: params.accountNumber,
    from: params.from,
    to: params.to,
    auxiliaryId: params.auxiliaryId,
    onlyLettered: params.onlyLettered || undefined,
    onlyUnreconciled: params.onlyUnreconciled || undefined,
    cursorDate: params.cursor?.date,
    cursorId: params.cursor?.id,
  });
}

/**
 * Pages `fetchLedger` to completion, so a caller never sees a `truncated`
 * partial result. `totals` is recomputed from the merged lines rather than
 * trusting any single page's totals (each page's own total is only correct
 * in isolation, per `generalLedger`'s server-side contract).
 */
async function fetchLedgerAllPages(params: LedgerParams): Promise<LedgerResponse> {
  const lines: LedgerLine[] = [];
  let cursor = params.cursor;
  let accountNumber = params.accountNumber;
  // Safety valve: a bug in `nextCursor` must not infinite-loop the tab.
  for (let page = 0; page < 1000; page++) {
    const response = await fetchLedger({ ...params, cursor });
    lines.push(...response.lines);
    accountNumber = response.accountNumber;
    if (!response.nextCursor) {
      let totalDebit = 0;
      let totalCredit = 0;
      for (const line of lines) {
        totalDebit += toNum(line.debit);
        totalCredit += toNum(line.credit);
      }
      return {
        accountNumber,
        lines,
        totals: { totalDebit, totalCredit, balance: totalDebit - totalCredit },
        nextCursor: null,
        truncated: false,
      };
    }
    cursor = response.nextCursor;
  }
  throw new Error('Grand livre : trop de pages à charger (garde-fou de sécurité atteint).');
}

async function fetchStatements(accountId?: string): Promise<StatementsResponse> {
  return apiClient.get<StatementsResponse>('/accounting/reconciliation/statements', {
    accountId,
  });
}

async function fetchStatement(id: string): Promise<{ statement: BankStatement }> {
  return apiClient.get<{ statement: BankStatement }>(
    `/accounting/reconciliation/statements/${id}`
  );
}

async function createStatement(
  input: CreateStatementInput
): Promise<{ statement: BankStatement }> {
  return apiClient.post<{ statement: BankStatement }>(
    '/accounting/reconciliation/statements',
    input
  );
}

async function matchStatementLine(
  input: MatchInput
): Promise<{ statementLine: BankStatementLine; journalLine: unknown }> {
  return apiClient.post(`/accounting/reconciliation/statements/${input.statementId}/match`, {
    statementLineId: input.statementLineId,
    journalLineId: input.journalLineId,
  });
}

async function unmatchStatementLine(
  input: UnmatchInput
): Promise<{ statementLine: BankStatementLine }> {
  return apiClient.post(
    `/accounting/reconciliation/statements/${input.statementId}/unmatch`,
    { statementLineId: input.statementLineId }
  );
}

async function validateStatement(id: string): Promise<ValidateStatementReport> {
  return apiClient.post<ValidateStatementReport>(
    `/accounting/reconciliation/statements/${id}/validate`
  );
}

async function fetchPostingStatus(): Promise<PostingStatusResponse> {
  return apiClient.get<PostingStatusResponse>('/accounting/posting-status');
}

async function fetchPostingWaiting(): Promise<PostingWaitingResponse> {
  return apiClient.get<PostingWaitingResponse>('/accounting/posting-waiting');
}

async function markPostedManually(outboxId: string): Promise<void> {
  await apiClient.post(`/accounting/posting-manual/${encodeURIComponent(outboxId)}`, {}, { noQueue: true, failWhenOffline: true });
}

async function retryPosting(): Promise<RetryPostingResult> {
  return apiClient.post<RetryPostingResult>('/accounting/posting-retry');
}

async function fetchMappings(): Promise<AccountMappingsResponse> {
  return apiClient.get<AccountMappingsResponse>('/accounting/mappings');
}

async function updateMapping(
  input: UpdateMappingInput
): Promise<AccountMappingsResponse> {
  return apiClient.put<AccountMappingsResponse>('/accounting/mappings', input);
}

// ─────────────────────────────────────────────────────────────
// Requêtes
// ─────────────────────────────────────────────────────────────

/** Plan comptable (sélecteurs) — GET /api/accounting/accounts */
export function useAccountingAccounts(
  params: AccountingAccountsParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: accountingOpsKeys.accounts(params),
    queryFn: () => fetchAccounts(params),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Lignes non lettrées d'un compte — GET
 * /api/accounting/lettering/:accountId/unlettered
 * `auxiliaryAccountId` (comptes tiers, spec 2026-07-24 §5.4) restreint
 * la liste aux lignes d'UN tiers.
 */
export function useUnletteredLines(
  accountId: string,
  options?: { enabled?: boolean; auxiliaryAccountId?: string | undefined }
) {
  return useQuery({
    queryKey: accountingOpsKeys.unlettered(accountId, options?.auxiliaryAccountId),
    queryFn: () => fetchUnletteredLines(accountId, options?.auxiliaryAccountId),
    enabled: (options?.enabled ?? true) && !!accountId,
  });
}

/**
 * Same endpoint, paged to completion: `lines` always covers the full
 * matching window, never a `truncated` slice of it. For a consumer that
 * needs every matching line (LetteringPage's existing letterings,
 * ReconciliationPage's candidate list) rather than a display page.
 */
export function useFullLedger(params: LedgerParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...accountingOpsKeys.ledger(params), 'all-pages'] as const,
    queryFn: () => fetchLedgerAllPages(params),
    enabled: (options?.enabled ?? true) && !!params.accountNumber,
  });
}

/** Relevés bancaires — GET /api/accounting/reconciliation/statements */
export function useBankStatements(accountId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingOpsKeys.statements(accountId),
    queryFn: () => fetchStatements(accountId),
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'un relevé — GET /api/accounting/reconciliation/statements/:id */
export function useBankStatement(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingOpsKeys.statement(id),
    queryFn: () => fetchStatement(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Suivi de la comptabilisation automatique — GET /api/accounting/posting-status */
export function usePostingStatus(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingOpsKeys.postingStatus(),
    queryFn: fetchPostingStatus,
    staleTime: 1000 * 30,
    enabled: options?.enabled ?? true,
  });
}

/** Règlements de documents provisoires en attente — GET /api/accounting/posting-waiting */
export function usePostingWaiting(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingOpsKeys.postingWaiting(),
    queryFn: fetchPostingWaiting,
    enabled: options?.enabled ?? true,
  });
}

/** Mappings rôle → compte — GET /api/accounting/mappings */
export function useAccountMappings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingOpsKeys.mappings(),
    queryFn: fetchMappings,
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/** The fully paged lettered-line ledgers (`useFullLedger` with `onlyLettered`) held in the cache. */
function letteredLedgers(queryClient: QueryClient): Array<[QueryKey, LedgerResponse]> {
  return queryClient
    .getQueriesData<LedgerResponse>({ queryKey: [...accountingOpsKeys.all, 'ledger'] })
    .filter((entry): entry is [QueryKey, LedgerResponse] => {
      const [key, data] = entry;
      const params = key[2] as LedgerParams | undefined;
      return data !== undefined && key[3] === 'all-pages' && params?.onlyLettered === true;
    });
}

/** `ledger` with `lines` in date order, their running balance and the totals recomputed. */
function withLines(ledger: LedgerResponse, lines: LedgerLine[]): LedgerResponse {
  const sorted = [...lines].sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  let totalDebit = 0;
  let totalCredit = 0;
  const withBalance = sorted.map((line) => {
    totalDebit += toNum(line.debit);
    totalCredit += toNum(line.credit);
    return { ...line, balance: totalDebit - totalCredit };
  });
  return { ...ledger, lines: withBalance, totals: { totalDebit, totalCredit, balance: totalDebit - totalCredit } };
}

/** An unlettered line as the ledger lists it once lettered by `letteringId`. */
function letteredLineOf(line: UnletteredLine, letteringId: string): LedgerLine {
  return {
    id: line.id,
    date: line.date,
    journalCode: line.journalCode,
    entryId: line.entryId,
    entryNumber: line.entryNumber,
    entryLabel: line.entryLabel,
    accountNumber: line.accountNumber,
    accountLabel: line.accountLabel,
    label: line.label,
    reference: line.entryReference,
    debit: line.debit,
    credit: line.credit,
    balance: 0,
    auxiliaryType: line.auxiliaryType,
    auxiliaryId: line.auxiliaryId,
    letteringId,
    reconciledAt: null,
    statementLineId: null,
  };
}

/**
 * Marks the other ledger views (grand livre pages, reconciliation) stale
 * without refetching them; the lettered ledgers in `kept` are left as patched.
 */
function markOtherLedgersStale(queryClient: QueryClient, kept: Set<string>): void {
  queryClient.invalidateQueries({
    queryKey: [...accountingOpsKeys.all, 'ledger'],
    refetchType: 'none',
    predicate: (query) => !kept.has(query.queryHash),
  });
}

/**
 * Lettrer un ensemble de lignes (équilibré, ou partiel) — POST /api/accounting/lettering
 *
 * The lettered ledgers of the account are updated in the cache from the
 * account's unlettered lines; only the unlettered lines are fetched again.
 * When the lettering holds a line the cache does not know (an exchange-
 * difference line it posted), that account's lettered ledger is fetched again.
 */
export function useCreateLettering() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLetteringInput) => createLettering(input),
    onSuccess: (data, input) => {
      const { lettering } = data;
      const unlettered = new Map<string, UnletteredLine>();
      for (const [, response] of queryClient.getQueriesData<UnletteredLinesResponse>({
        queryKey: [...accountingOpsKeys.all, 'unlettered', input.accountId],
      })) {
        for (const line of response?.lines ?? []) unlettered.set(line.id, line);
      }
      const accountNumber = input.lineIds.map((id) => unlettered.get(id)?.accountNumber).find(Boolean);
      const lettered = new Set(lettering.lineIds);
      const kept = new Set<string>();
      for (const [key, ledger] of letteredLedgers(queryClient)) {
        if ((key[2] as LedgerParams).accountNumber !== accountNumber) continue;
        const known = new Map(ledger.lines.map((line) => [line.id, line]));
        if (!lettering.lineIds.every((id) => known.has(id) || unlettered.has(id))) {
          queryClient.invalidateQueries({ queryKey: key, exact: true });
          continue;
        }
        const added = lettering.lineIds.map((id) => {
          const line = known.get(id);
          return line ? { ...line, letteringId: lettering.id } : letteredLineOf(unlettered.get(id)!, lettering.id);
        });
        queryClient.setQueryData(key, withLines(ledger, [...ledger.lines.filter((line) => !lettered.has(line.id)), ...added]));
        kept.add(queryClient.getQueryCache().find({ queryKey: key, exact: true })!.queryHash);
      }
      // Préfixe SANS le segment tiers : couvre la vue « tous » ET toutes
      // les variantes filtrées par tiers du compte.
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'unlettered', input.accountId],
      });
      markOtherLedgersStale(queryClient, kept);
    },
  });
}

/**
 * Délettrage — DELETE /api/accounting/lettering/:id
 *
 * The lettering's lines leave the lettered ledgers in the cache; only the
 * account's unlettered lines are fetched again. Undoing a lettering that
 * posted exchange differences letters their reversals anew: that
 * account's lettered ledger is fetched again.
 */
export function useDeleteLettering() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLettering(id),
    onSuccess: (data) => {
      const { delettrage } = data;
      const kept = new Set<string>();
      for (const [key, ledger] of letteredLedgers(queryClient)) {
        if (!ledger.lines.some((line) => line.letteringId === delettrage.letteringId)) continue;
        if (delettrage.fxReversalEntryIds.length > 0) {
          queryClient.invalidateQueries({ queryKey: key, exact: true });
          continue;
        }
        queryClient.setQueryData(
          key,
          withLines(ledger, ledger.lines.filter((line) => line.letteringId !== delettrage.letteringId))
        );
        kept.add(queryClient.getQueryCache().find({ queryKey: key, exact: true })!.queryHash);
      }
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'unlettered', delettrage.accountId],
      });
      markOtherLedgersStale(queryClient, kept);
    },
  });
}

/** Saisie d'un relevé bancaire — POST /api/accounting/reconciliation/statements */
export function useCreateBankStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStatementInput) => createStatement(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'statements'],
      });
    },
  });
}

/** Rapprocher une ligne de relevé — POST .../statements/:id/match */
export function useMatchStatementLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MatchInput) => matchStatementLine(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: accountingOpsKeys.statement(input.statementId),
      });
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'statements'],
      });
    },
  });
}

/** Annuler un rapprochement — POST .../statements/:id/unmatch */
export function useUnmatchStatementLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UnmatchInput) => unmatchStatementLine(input),
    onSuccess: (_data, input) => {
      queryClient.invalidateQueries({
        queryKey: accountingOpsKeys.statement(input.statementId),
      });
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'statements'],
      });
    },
  });
}

/** Valider un relevé rapproché — POST .../statements/:id/validate */
export function useValidateStatement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => validateStatement(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: accountingOpsKeys.statement(id),
      });
      queryClient.invalidateQueries({
        queryKey: [...accountingOpsKeys.all, 'statements'],
      });
    },
  });
}

/** Relancer les comptabilisations en échec — POST /api/accounting/posting-retry */
export function useRetryPosting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryPosting,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: accountingOpsKeys.postingStatus(),
      });
    },
  });
}

/**
 * Marquer « Comptabilisé manuellement » le règlement d'un document provisoire
 * — POST /api/accounting/posting-manual/:id. Jamais mis en file hors ligne.
 */
export function useMarkPostedManually() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markPostedManually,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountingOpsKeys.postingWaiting() });
      void queryClient.invalidateQueries({ queryKey: accountingOpsKeys.postingStatus() });
    },
  });
}

/** Surcharger le compte d'un rôle — PUT /api/accounting/mappings */
export function useUpdateAccountMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMappingInput) => updateMapping(input),
    onSuccess: (data) => {
      queryClient.setQueryData(accountingOpsKeys.mappings(), data);
    },
  });
}
